<template>
  <div class="BookingContextRead">
    <div class="BookingContextRead--desktop">
      <div class="BookingContextRead-info">
        <div class="BookingContextRead-row">
          <ai-icon name="location" class="BookingContextRead-row-icon" />

          <ai-typo variant="paragraph-04-bold" as="span" color="stratos-900">
            {{ hotelName }}
          </ai-typo>
        </div>

        <ai-separator :margin="0" vertical />

        <div class="BookingContextRead-row">
          <ai-icon name="calendar" class="BookingContextRead-row-icon" />
          <ai-typo variant="paragraph-04-bold" as="span" color="stratos-900">
            {{ datesLabel }}
          </ai-typo>
        </div>

        <ai-separator :margin="0" vertical />

        <div class="BookingContextRead-row">
          <ai-icon name="guest" class="BookingContextRead-row-icon" />
          <ai-typo variant="paragraph-04-bold" as="span" color="stratos-900">
            {{ guestsLabels }}
          </ai-typo>
        </div>
      </div>
      <div class="BookingContextRead-edit">
        <ai-button
          class="BookingContextRead-EditButton"
          variant="tertiary"
          icon-right="edit"
          :label="$t('booking.context.edit')"
          @click="$emit('edit', { isMobile: false })" />
      </div>
    </div>

    <div class="BookingContextRead--mobile">
      <ul class="BookingContextRead-list">
        <li class="BookingContextRead-listItem">
          <ai-icon name="location" class="BookingContextRead-listItem-icon" />
          <ai-typo as="span" variant="paragraph-04-bold">
            {{ hotelName }}
          </ai-typo>
        </li>

        <li class="BookingContextRead-listItem">
          <ai-icon name="calendar" class="BookingContextRead-listItem-icon" />
          <ai-typo as="span" variant="paragraph-04-bold">
            {{ datesLabel }}
          </ai-typo>
        </li>

        <li class="BookingContextRead-listItem">
          <ai-icon name="guest" class="BookingContextRead-listItem-icon" />
          <ai-typo as="span" variant="paragraph-04-bold">
            {{ guestsLabels }}
          </ai-typo>
        </li>
      </ul>

      <ai-button variant="tertiary" slim>
        <ai-icon
          name="edit"
          :size="32"
          @click="$emit('edit', { isMobile: true })" />
      </ai-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { format } from 'date-fns';

import { useBookingStore } from '../../store';

import { FORMATS } from '~~/helpers/date.helper';

type Props = {
  hotelName: string;
  roomsAndGuests: { adults: number; childrenAges: number[] }[];
};

type Emits = {
  (event: 'edit', payload: { isMobile: boolean }): void;
};

const props = defineProps<Props>();
defineEmits<Emits>();

const { t } = useI18n();
const dateFnsLocale = useDateFnsLocale();
const bookingStore = useBookingStore();

let lastUsedDate: string = '';
let lastUsedNRooms: string = '';

const datesLabel = computed(() => {
  // prevent date disappearing at refresh moment (display old value)
  if (!bookingStore.isoDateIn) {
    return lastUsedDate;
  }

  const numberOfNights = bookingStore.nights;
  const rawFirstDate = new Date(bookingStore.isoDateIn as string);
  const rawLastDate = new Date(bookingStore.isoDateOut as string);

  const firstDate = format(rawFirstDate, FORMATS.inputFormat, {
    locale: dateFnsLocale.value,
  });
  const lastDate = format(rawLastDate, FORMATS.inputFormat, {
    locale: dateFnsLocale.value,
  });

  lastUsedDate = t('booking.bar.mobile.datesWithCount', numberOfNights, {
    named: {
      count: numberOfNights,
      firstDate,
      lastDate,
    },
  });

  return lastUsedDate;
});

const guestsLabels = computed(() => {
  const rooms = (props.roomsAndGuests || []).length;
  const guests = bookingStore.totalGuestsCount;

  if (!rooms || !guests) {
    return lastUsedNRooms;
  }

  const roomsLabel = t('booking.bar.inputs.roomAndGuest.room', {
    count: rooms,
  });

  const guestsLabel = t('booking.bar.inputs.roomAndGuest.guest', {
    count: guests,
  });

  lastUsedNRooms = t('booking.bar.inputs.roomAndGuest.fullValue', {
    guests: guestsLabel,
    rooms: roomsLabel,
  });
  return lastUsedNRooms;
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/themes/rixos/constants' as RixosTheme;

.BookingContextRead--desktop {
  display: none;
  justify-content: space-between;
  background-color: colors.$gold-100;
  padding: constants.$padding-02;

  @media (mq.$from-large) {
    display: flex;
  }
}

.BookingContextRead-info {
  display: flex;
  gap: constants.$margin-03;
}

.BookingContextRead-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.BookingContextRead-row-icon {
  color: colors.$gold-900;
  margin-right: constants.$margin-00;
}

.BookingContextRead-EditButton {
  text-transform: uppercase;
}

.BookingContextRead--mobile {
  display: flex;
  align-items: center;
  background-color: colors.$gold-100;
  padding-top: constants.$padding-02;
  padding-bottom: constants.$padding-02;
  padding-left: constants.$padding-01;
  padding-right: constants.$padding-01;

  @media (mq.$from-large) {
    display: none;
  }
}

.BookingContextRead-list {
  flex-grow: 1;
}

.BookingContextRead-listItem {
  display: flex;
  flex-direction: row;
  align-items: center;

  &:not(:last-of-type) {
    margin-bottom: constants.$inner-01;
  }
}

.BookingContextRead-listItem-icon {
  margin-right: constants.$margin-00;
  color: colors.$gold-900;
}

.theme.rixos {
  .BookingContextRead {
    .AiIcon,
    .AiButton {
      color: RixosTheme.$color-primary-light;
    }

    :deep(.BookingContextRead-edit .AiTypo),
    .AiTypo {
      color: colors.$white;
    }
  }

  .BookingContextRead--mobile,
  .BookingContextRead--desktop {
    background-color: RixosTheme.$color-primary-black;
  }
}
</style>
